"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MonarchClient = exports.MonarchFailedRequestError = exports.MonarchBadResponseError = exports.MonarchBadInputError = void 0;
const axios_1 = require("axios");
const errors_1 = require("./errors");
var errors_2 = require("./errors");
Object.defineProperty(exports, "MonarchBadInputError", { enumerable: true, get: function () { return errors_2.MonarchBadInputError; } });
Object.defineProperty(exports, "MonarchBadResponseError", { enumerable: true, get: function () { return errors_2.MonarchBadResponseError; } });
Object.defineProperty(exports, "MonarchFailedRequestError", { enumerable: true, get: function () { return errors_2.MonarchFailedRequestError; } });
/**
 * Instances of MonarchClient can evaluate rules and rule sets for a given monarch source.
 * Create instances of the MonarchClient by passing in the source's source ID and client token.
 * You can also optionally pass options to the client.
 *
 * @example
 * const rulesEngine1 = new MonarchClient('source_id_here', 'client_token_here');
 * const rulesEngine2 = new MonarchClient('source_id_here', 'client_token_here', {
 *  timeout: 1000
 * });
 */
class MonarchClient {
    /**
     * Create a monarch client scoped to the given source ID
     * You can also optionally pass options to the client.
     *
     * @example
     * const rulesEngine1 = new MonarchClient('source_id_here', 'client_token_here');
     * const rulesEngine2 = new MonarchClient('source_id_here', 'client_token_here', {
     *  timeout: 1000
     * });
     */
    constructor(sourceId, clientToken, options) {
        var _a;
        if (!sourceId) {
            throw new errors_1.MonarchBadInputError('Missing source ID in MonarchClient constructor');
        }
        if (!clientToken) {
            throw new errors_1.MonarchBadInputError('Missing client token in MonarchClient constructor');
        }
        this._sourceId = sourceId;
        this._axiosInstance = axios_1.default.create({
            baseURL: 'https://monarch.cohesionapps.com',
            timeout: (_a = options === null || options === void 0 ? void 0 : options.timeout) !== null && _a !== void 0 ? _a : 1000,
            headers: {
                'Content-Type': 'application/json',
                token: clientToken,
            },
            withCredentials: false,
        });
    }
    /**
     * Evaluate a rule given the rule ID.
     * Optionally add a body if the rule requires custom subjects.
     * Optionally add headers in the `options` object.
     *
     * By default, this method will return the return value of the rule or null if nothing was returned.
     * To return the entire response payload, the fourth parameter must be `true`.
     *
     * @example
     * const monarchClient = new MonarchClient('source_id_here', 'client_token_here');
     * // The following makes a request to monarch sending `{ userEmail: 'name@redventures.com }` in the body
     * // If the rule returns a string of `bannerA`, then `res1` will equal `bannerA`
     * const res1 = await evaluateRule('rule_id_here', { userEmail: 'name@redventures.com' });
     * // The following makes the same request to monarch, but `res` is equal to the entire payload
     * // That is, `res` is an object `{ 'rule_id_here': 'bannerA' }`
     * const res2 = await evaluateRule('rule_id_here', { userEmail: 'name@redventures.com', null, true});
     * // Note that in TypeScript, one can add a generic parameter to type the return value
     * const res3 = await evaluateRule<string>('rule_id_here', { userEmail: 'name@redventures.com' });
     */
    evaluateRule(ruleId, body, options, returnFullPayload) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!ruleId) {
                throw new errors_1.MonarchBadInputError('Missing rule ID in MonarchClient.evaluateRule');
            }
            const res = yield this._makeRequest('rule', ruleId, body, options);
            if (returnFullPayload) {
                return res;
            }
            const resKeys = Object.keys(res);
            // if the response contained no return values, return null
            if (!resKeys.length)
                return null;
            // grab the rule's UUID/custom ID out of the response
            const [ruleKey] = resKeys;
            return res[ruleKey];
        });
    }
    /**
     * Evaluate a rule set given the rule set ID.
     * Optionally add a body if the rules in the rule set requires custom subjects.
     * Optionally add headers in the `options` object.
     *
     * @example
     * const monarchClient = new MonarchClient('source_id_here', 'client_token_here');
     * // The following makes a request to monarch to evaluate a rule set with the given body
     * // This function returns an object where each key is a rule ID of a rule
     * // that returned a return value and the value of that key is the return value
     * const res1 = await evaluateRuleSet('rule_set_id_here', { userEmail: 'name@redventures.com' });
     * // Note that in TypeScript, one can add a generic parameter to type the return values of each rule
     * interface RuleSetReturn {
     *   'rule_id_1': string;
     *   'rule_id_2': number;
     *   'rule_id_3': {
     *     msg: string;
     *     status: boolean;
     *   }
     * }
     * const res2 = await evaluateRuleSet<RuleSetReturn>('rule_set_id_here', { userEmail: 'name@redventures.com' });
     */
    evaluateRuleSet(ruleSetId, body, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!ruleSetId) {
                throw new errors_1.MonarchBadInputError('Missing rule set ID in MonarchClient.evaluateRuleSet');
            }
            const res = yield this._makeRequest('ruleset', ruleSetId, body, options);
            return res;
        });
    }
    _makeRequest(resourceType, id, body, options) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const headers = this._getAdditionalHeaders(options);
                const payload = Object.assign({}, (body !== null && body !== void 0 ? body : {}));
                if (options === null || options === void 0 ? void 0 : options.forceReturns) {
                    payload._Monarch = {
                        forceReturns: options.forceReturns,
                    };
                    if (options.allowInvalidForcedReturns) {
                        payload._Monarch.allowInvalidForcedReturns = true; // eslint-disable-line no-underscore-dangle
                    }
                }
                const res = yield this._axiosInstance.post(`/api/v1/evaluate/${resourceType}/${this._sourceId}/${id}`, payload, { headers });
                return res.data;
            }
            catch (e) {
                // Treat first as an axios error
                const axiosErr = e;
                if (axiosErr.response) {
                    throw new errors_1.MonarchBadResponseError(`Monarch failed to evaluate ${resourceType} ID ${id} with status code ${axiosErr.response.status}`, resourceType, id, axiosErr.response.status, axiosErr.response.data);
                }
                // Treat as generic error
                const err = e;
                throw new errors_1.MonarchFailedRequestError(`Monarch failed to evaluate ${resourceType} ID ${id}`, resourceType, id, err);
            }
        });
    }
    _getAdditionalHeaders(options) {
        const headers = {};
        if (options) {
            if (options.requestUrl) {
                headers['Monarch-Request-URL'] = options.requestUrl;
            }
            if (options.referrerUrl) {
                headers['Monarch-Referrer-URL'] = options.referrerUrl;
            }
            if (options.writeKeyOverride) {
                headers['Make-Write-Key'] = options.writeKeyOverride;
            }
            if (options.makeSourceUid) {
                headers['Make-Source-Uid'] = options.makeSourceUid;
            }
            if (options.segmentExternalId) {
                headers['Segment-External-Id'] = options.segmentExternalId;
            }
            if (options.makeIdentifier) {
                headers['Make-Identifier'] = options.makeIdentifier;
            }
            if (options.sessionId) {
                headers['Session-Id'] = options.sessionId;
            }
            if (options.crossSiteId) {
                headers['Cross-Site-Id'] = options.crossSiteId;
            }
            if (options.anonymousId) {
                headers['Anonymous-Id'] = options.anonymousId;
            }
            if (options.instanceId) {
                headers['Instance-Id'] = options.instanceId;
            }
            if (options.hashId) {
                headers['Hash-Id'] = options.hashId;
            }
            if (options.context) {
                headers.Context = options.context;
            }
            if (options.hasConsent != null) {
                headers.Consent = options.hasConsent ? 'true' : 'false';
            }
        }
        return headers;
    }
}
exports.MonarchClient = MonarchClient;
